/*
 * @Author: czj
 * @Date: 2026-06-13 15:51:10
 * @Description: 用户认证 & 项目配置 Store — 参照 genostack 合并
 * @FilePath: \kp-rag-system\src\Store\user.js
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  login,
  phone_login,
  email_login,
  user_registration,
  get_user_info,
} from '@/api/user'
import { system_conf } from '@/api/system'
import { encryptAES } from '@/utils/cryptoJS'
import { getToken, setToken, removeToken, setTicket, removeTicket } from '@/utils/auth'

export const useUserStore = defineStore('user', () => {
  // ========== State ==========
  const token = ref(getToken() || '')
  const name = ref('')
  const userInfo = ref({})
  const avatar = ref('')
  const roles = ref([])
  const hasUser = ref(false)

  // 项目配置（由 getSystem_conf 填充）
  const project_name = ref('')
  const project_img = ref('')
  const project_info = ref({})

  // ========== Actions ==========

  /** 账号密码登录 */
  async function loginAction(form) {
    const res = await login({
      name: form.account.trim(),
      password: encryptAES(form.password),
      grant_type: 'password',
      client_id: '2',
      openid: form.openid || '',
      client_secret: 'hDTfAmRMNwmNqKS5ff0MZ4XiSdt7cxS9YzLh7ix2',
    })
    token.value = res.access_token
    setToken(res.access_token)
    // 兼容两种 ticket key
    if (res.ticket) setTicket(res.ticket)
    return res
  }

  /** 手机号登录 */
  async function loginByPhone(form) {
    const res = await phone_login(form)
    token.value = res.access_token
    setToken(res.access_token)
    if (res.ticket) setTicket(res.ticket)
    return res
  }

  /** 邮箱登录 */
  async function loginByEmail(form) {
    const res = await email_login(form)
    token.value = res.access_token
    setToken(res.access_token)
    if (res.ticket) setTicket(res.ticket)
    return res
  }

  /** 用户注册 */
  async function registerAction(form) {
    const res = await user_registration(form)
    if (res.access_token) {
      token.value = res.access_token
      setToken(res.access_token)
      if (res.ticket) setTicket(res.ticket)
    }
    return res
  }

  /** 获取当前用户信息 */
  async function getUserInfo() {
    const res = await get_user_info()
    hasUser.value = true
    name.value = res.data?.name || res.name || ''
    userInfo.value = res.data || res
    return res
  }

  /** 获取系统配置（主题色/项目名称/Logo/字体等） */
  async function getSystem_conf() {
    try {
      const res = await system_conf()
      if (!res) return

      // 持久化到 localStorage
      localStorage.setItem('system_config', JSON.stringify(res))

      // 应用 CSS 变量（主题色 + 字体）
      if (res.theme_color) {
        document.documentElement.style.setProperty('--primary-color', res.theme_color)
      }
      if (res.theme_fonts && res.theme_fonts.length > 0) {
        document.documentElement.style.setProperty('--global-font', res.theme_fonts[0])
      }

      // 更新项目信息
      project_name.value = res.name || ''
      project_img.value = res.img || ''
      project_info.value = res
      document.title = res.name || 'KP-RAG'

      // 更新 meta 标签
      const metaList = document.getElementsByTagName('meta')
      for (let i = 0; i < metaList.length; i++) {
        const nameAttr = metaList[i].getAttribute('name')
        if (nameAttr === 'description' && res.description) {
          metaList[i].content = res.description
        }
        if (nameAttr === 'keywords' && res.s_keywords) {
          metaList[i].content = res.s_keywords
        }
      }

      // 存储右侧工具栏信息
      if (res.rightinfo) {
        window['rightinfo'] = res.rightinfo
      }
      if (res.RightButton) {
        window['right_buttons'] = res.RightButton
      }
      return res
    } catch (e) {
      console.error('获取系统配置失败:', e)
    }
  }

  /** 清除 Token（不清除路由） */
  function resetToken() {
    hasUser.value = false
    token.value = ''
    roles.value = []
    name.value = ''
    userInfo.value = {}
    removeToken()
    removeTicket()
  }

  /** 退出登录 — 清除状态并跳转 */
  async function logoutAction() {
    resetToken()
  }

  return {
    // state
    token,
    name,
    userInfo,
    avatar,
    roles,
    hasUser,
    project_name,
    project_img,
    project_info,
    // actions
    loginAction,
    loginByPhone,
    loginByEmail,
    registerAction,
    getUserInfo,
    getSystem_conf,
    resetToken,
    logoutAction,
  }
})
